import Head from 'next/head';
import { useRouter } from 'next/router';

const ORG_ID = 'https://kreatli.com/#organization';
const WEBSITE_ID = 'https://kreatli.com/#website';
const SOFTWARE_ID = 'https://kreatli.com/#software';

const organizationData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'Kreatli',
  url: 'https://kreatli.com',
  logo: 'https://kreatli.com/web-app-manifest-512x512.png',
  description:
    'Video Collaboration & Review Platform for creative teams. Timecoded feedback, project management, and file review in one workspace.',
  sameAs: [
    'https://www.linkedin.com/company/kreatli',
    'https://x.com/kreatli',
    'https://www.facebook.com/people/Kreatli/61581649865831',
    'https://www.youtube.com/@kreatli',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@kreatli.com',
    contactType: 'customer support',
  },
};

const serviceData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      '@id': 'https://kreatli.com/#service-video-review',
      name: 'Video review and approval',
      url: 'https://kreatli.com/platform/review-approval',
      description: 'Video review software with timecoded comments, guest review links and sign-off on file versions.',
      provider: { '@id': ORG_ID },
    },
    {
      '@type': 'Service',
      '@id': 'https://kreatli.com/#service-creative-proofing',
      name: 'Creative proofing',
      url: 'https://kreatli.com/solutions/use-case/creative-proofing',
      description: 'Comment on files, compare versions, and turn feedback into trackable work.',
      provider: { '@id': ORG_ID },
    },
    {
      '@type': 'Service',
      '@id': 'https://kreatli.com/#service-workspace',
      name: 'Creative project workspace',
      url: 'https://kreatli.com/platform',
      description: 'Tasks, files, reviews, and conversations in one video production workspace.',
      provider: { '@id': ORG_ID },
    },
  ],
};

const websiteData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: 'Kreatli',
  url: 'https://kreatli.com',
  publisher: { '@id': ORG_ID },
};

const softwareData = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': SOFTWARE_ID,
  name: 'Kreatli',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: 'https://kreatli.com',
  description:
    'Video Collaboration & Review Platform for creative teams. Timecoded video review, project management, and approval workflows.',
  provider: { '@id': ORG_ID },
  offers: {
    '@type': 'AggregateOffer',
    lowPrice: '14',
    highPrice: '29',
    priceCurrency: 'USD',
    offerCount: '3',
  },
};

/** SoftwareApplication has no ratings to show, so it is only emitted where the product itself is the topic. */
const SOFTWARE_SCHEMA_PATHS = new Set(['/', '/pricing']);

/**
 * Global structured data for Organization and WebSite schemas (every page) plus SoftwareApplication
 * (home and pricing only). Render once in _app.tsx.
 */
export function OrganizationStructuredData() {
  const { pathname } = useRouter();

  return (
    <Head>
      <script
        key="org-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationData) }}
      />
      <script
        key="website-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteData) }}
      />
      {SOFTWARE_SCHEMA_PATHS.has(pathname) ? (
        <script
          key="software-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareData) }}
        />
      ) : null}
      <script
        key="service-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceData) }}
      />
    </Head>
  );
}
